/**
 * What a `.git` folder says, read as two text files.
 *
 * The app asks git — `git rev-parse`, `git remote get-url` — because it has git.
 * This page has a folder somebody granted their browser and no process to run,
 * so the two answers a published project needs come out of `.git/config` and
 * `.git/HEAD` directly.
 *
 * Both are needed and for different reasons. The **remote** is the project's
 * identity (`workspace/remote.ts`): it is what a member's existing checkout is
 * matched on and what a member without one clones, so a project whose remote
 * were left empty would be a project nobody else can obtain. The **branch** is
 * the one a session starts from.
 *
 * Nothing here is trusted: a config file arrives with a folder somebody picked,
 * and the URL it names goes through `normalizeRemote` before it becomes an
 * identity — which is what refuses a path on this machine, a `file://` and a
 * credential pasted into a URL.
 */

/** The preference order, which is the app's own (`git-source.ts`). */
const FALLBACK_REMOTE = 'origin';

export interface GitIdentity {
  /** The checked-out branch, or `null` for a detached or unborn HEAD. */
  branch: string | null;
  /** The remote URL as the repository spells it, or `null` when it has none. */
  remoteUrl: string | null;
}

/**
 * `ref: refs/heads/some/branch` — a branch name may hold slashes, so everything
 * past `refs/heads/` is the name. A detached HEAD is a bare object id, which is
 * not a branch and reads as none.
 */
export function branchFromHead(head: string | null): string | null {
  const ref = /^\s*ref:\s*refs\/heads\/(.+?)\s*$/m.exec(head ?? '');
  return ref ? ref[1] : null;
}

/**
 * Every remote's URL, by name, in the order the file names them.
 *
 * A hand-rolled reader rather than a config parser: this file has one shape that
 * matters — a `[remote "name"]` section with a `url` in it — and git's full
 * grammar (includes, conditional includes, `\` continuations) is a great deal of
 * surface for a prefill. Anything it does not understand reads as "no remote",
 * which leaves the field for the person to paste into.
 */
export function remotesFromConfig(config: string | null): Map<string, string> {
  const out = new Map<string, string>();
  let remote: string | null = null;
  for (const line of (config ?? '').split(/\r?\n/)) {
    const section = /^\s*\[([^\]]*)\]/.exec(line);
    if (section) {
      // `[remote "origin"]`, and also the subsection-less `[remote]` that is not
      // one. A quoted name may hold an escaped quote; a remote's never does.
      const named = /^remote\s+"(.*)"$/.exec(section[1].trim());
      remote = named ? named[1] : null;
      continue;
    }
    if (!remote) continue;
    const url = /^\s*url\s*=\s*(.*?)\s*$/.exec(line);
    // The first `url` of a section wins, as git's own last-one-wins does not
    // matter here: a remote with two is a `pushurl` away from ordinary.
    if (url && url[1] && !out.has(remote)) out.set(remote, url[1]);
  }
  return out;
}

/** Which remote the checked-out branch tracks, when it says. */
function trackedBy(config: string | null, branch: string | null): string | null {
  if (!branch) return null;
  const escaped = branch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const found = new RegExp(String.raw`^\s*\[branch\s+"${escaped}"\][^[]*?^\s*remote\s*=\s*(.*?)\s*$`, 'ms').exec(
    config ?? ''
  );
  return found?.[1] || null;
}

/**
 * The identity of a checkout, from the two files that hold it.
 *
 * The remote is the one the checked-out branch tracks, then `origin`, then
 * whichever the file names first — the app's order, for the same reason: a
 * repository with a fork and an upstream is addressed by the one its branch
 * pushes to, and a repository with one remote under any name still has one.
 */
export function gitIdentity(config: string | null, head: string | null): GitIdentity {
  const branch = branchFromHead(head);
  const remotes = remotesFromConfig(config);
  const tracked = trackedBy(config, branch);
  const name = [tracked, FALLBACK_REMOTE].find((one) => one && remotes.has(one)) ?? [...remotes.keys()][0];
  return { branch, remoteUrl: (name && remotes.get(name)) ?? null };
}
